/**
 * FeedbackScreenshotsPickerGrid — the editable screenshots grid on the
 * add-feedback page (`FeedbackPage`).
 *
 * Web port of Flutter's `_screenshotsGrid`
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:364-471`): a
 * grid where `itemCount == min(length + 1, 3)` (`:463`). Each filled cell renders
 * the locally-picked screenshot with a tap-to-view target
 * (`_onScreenshotTapped`, `:412-422`) plus a bin/remove overlay
 * (`assets/images/bin.png`, `_onRemoveScreenshotTapped`, `:423-440`). The
 * trailing "add" cell — present only while `length < 3` — taps to open the
 * picker (`_onUploadTapped`, `:445-460`) and shows `assets/images/add.png`.
 *
 * This is the local/editable counterpart to the read-only admin
 * {@link FeedbackScreenshotsGrid}; it is a **pure presentational** component —
 * it owns no state, invokes no use case/service, opens no sheet and never
 * navigates. The page (Task 10) supplies the draft list + the callbacks
 * (`useFeedback` owns the draft state). The two cell subcomponents live in their
 * own colocated files ({@link FeedbackPickerImageCell},
 * {@link FeedbackPickerAddCell}).
 *
 * Web deviations (directed by the task plan):
 * - Rendered via the shared {@link VirtualList} in grid mode — the same
 *   virtualized grid the read-only sibling {@link FeedbackScreenshotsGrid} uses.
 *   Both cell kinds (a screenshot cell and the trailing add cell) flow through a
 *   single `renderItem` via a {@link PickerCell} discriminated union. The shared
 *   `CONTENT_CONSTANTS.gridLayout` spacing/aspect tokens are reused so the picker
 *   matches every other media grid.
 * - No `_uploadingIndex` spinner on the add cell (`:448-451`): on the web the
 *   picking happens inside `AddContentSheet`, whose own Drop-step spinner covers
 *   the wait, so the inline Flutter spinner has no web equivalent.
 * - Square cells (`CONTENT_CONSTANTS.gridLayout.itemAspectRatio`, `1`) holding
 *   the screenshot centered at its own natural aspect ratio (see
 *   {@link FeedbackPickerImageCell}) — rather than Flutter's fixed
 *   `childAspectRatio: 3/4` cover thumbnail.
 * - Responsive column count (`useGridColumnsCount` — 3 portrait / 5 landscape)
 *   like the read-only sibling and every other media grid, rather than Flutter's
 *   fixed `crossAxisCount: 3`. The max-3 screenshot cap
 *   (`MAX_FEEDBACK_SCREENSHOTS`) is decoupled from the column count: the trailing
 *   add cell is appended only while `length < MAX_FEEDBACK_SCREENSHOTS`, so a
 *   landscape row never shows a dead add affordance. `VirtualList` grid mode
 *   auto-fills any surplus columns in the last row with `aria-hidden` spacers, so
 *   the filled cells keep a consistent width.
 * - Both glyphs are theme-masked (`AssetIcon` over a transparent silhouette),
 *   using the theme-aware `theme.icon.primary` (visible in both themes) instead
 *   of Flutter's fixed `whiteLight`, which would vanish on the light-theme
 *   surface. The bin glyph sits on a circular `theme.danger` backdrop as the
 *   remove affordance — matching Flutter's `Image.asset('assets/images/bin.png')`
 *   (`:430-438`).
 */
import { useCallback, useMemo } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useGridColumnsCount } from '@/presentation/hooks';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { FeedbackPickerImageCell } from '@/presentation/feature/feedback/add/components/FeedbackPickerImageCell';
import { FeedbackPickerAddCell } from '@/presentation/feature/feedback/add/components/FeedbackPickerAddCell';
import { MAX_FEEDBACK_SCREENSHOTS } from '@/domain/constants/feedbackConstants';
import type { LocalFeedbackScreenshot } from '@/presentation/feature/feedback/add/hooks/useFeedback';
import { AddFeedbackScreenshotCellType } from '@/domain/enums/feedback/addFeedbackScreenshotCellType';

/**
 * A single cell of the picker grid — either a filled screenshot cell or the
 * trailing "add" cell — so both kinds flow through one `VirtualList` renderItem.
 */
type PickerCell =
  | { kind: AddFeedbackScreenshotCellType.screenshot; screenshot: LocalFeedbackScreenshot; index: number }
  | { kind: AddFeedbackScreenshotCellType.add };

/** Stable key for the single trailing add cell. */
const ADD_CELL_KEY = 'feedback-picker-add';

export interface FeedbackScreenshotsPickerGridProps {
  /** The locally-picked draft screenshots (max 3), from `useFeedback`. */
  screenshots: LocalFeedbackScreenshot[];
  /** Tap a filled cell → open the local viewer (`_onScreenshotTapped`, `:413`). */
  onScreenshotTap: (index: number) => void;
  /** Tap a cell's bin → drop that screenshot (`_onRemoveScreenshotTapped`, `:425`). */
  onRemoveTap: (index: number) => void;
  /** Tap the add cell → open the picker (`_onUploadTapped`, `:446`). */
  onAddTap: () => void;
}

export const FeedbackScreenshotsPickerGrid = ({
  screenshots,
  onScreenshotTap,
  onRemoveTap,
  onAddTap,
}: FeedbackScreenshotsPickerGridProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  // Responsive column count — 3 portrait / 5 landscape — mirroring the read-only
  // sibling FeedbackScreenshotsGrid and every other media grid, rather than a
  // hardcoded 3 (Flutter uses a fixed `crossAxisCount: 3`). Both values are > 1
  // so VirtualList grid mode engages.
  const gridColumnsCount = useGridColumnsCount();

  // One screenshot cell per draft, then a single trailing add cell appended only
  // while under the max (`MAX_FEEDBACK_SCREENSHOTS`): the add affordance
  // disappears once the max screenshots are present and reappears when one is
  // removed. Parity with Flutter's `itemCount: min(length + 1, 3)` (`:463`);
  // decoupling the cap from the (responsive) column count keeps a landscape row
  // from ever rendering a dead add cell in its surplus columns.
  const cells = useMemo<PickerCell[]>(() => {
    const screenshotCells: PickerCell[] = screenshots.map((screenshot, index) => ({
      kind: AddFeedbackScreenshotCellType.screenshot,
      screenshot,
      index,
    }));
    if (screenshots.length < MAX_FEEDBACK_SCREENSHOTS) {
      screenshotCells.push({ kind: AddFeedbackScreenshotCellType.add });
    }
    return screenshotCells;
  }, [screenshots]);

  const renderCell = useCallback(
    (cell: PickerCell) => {
      if (cell.kind === AddFeedbackScreenshotCellType.screenshot) {
        return (
          <FeedbackPickerImageCell
            screenshot={cell.screenshot}
            index={cell.index}
            onScreenshotTap={onScreenshotTap}
            onRemoveTap={onRemoveTap}
          />
        );
      }
      return <FeedbackPickerAddCell onAddTap={onAddTap} />;
    },
    [onScreenshotTap, onRemoveTap, onAddTap],
  );

  const getCellKey = useCallback(
    (cell: PickerCell) =>
      cell.kind === AddFeedbackScreenshotCellType.screenshot
        ? `${cell.screenshot.previewUrl}-${cell.index}`
        : ADD_CELL_KEY,
    [],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.spacing.sm,
      }}
    >
      {/* Section title (`:371-374`). */}
      <p
        style={{
          margin: 0,
          color: theme.text.primary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {l10n.feedback.uploadScreenshotsTitle}
      </p>

      {/* Surface card wrapping the virtualized grid (`:379-393`). The grid sits
          in normal page flow like the read-only sibling, so VirtualList can
          measure its container width to compute the square row height. */}
      <div
        style={{
          background: theme.surface,
          borderRadius: responsive.borderRadius.base,
          padding: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        }}
      >
        <VirtualList<PickerCell>
          items={cells}
          renderItem={renderCell}
          getItemKey={getCellKey}
          gridProps={{
            columnsCount: gridColumnsCount,
            crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
            mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
            itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
            bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
          }}
          backgroundColor={theme.surface}
          qa={{ id: 'feedback-screenshots-picker-grid' }}
        />
      </div>
    </div>
  );
};
